import { FC, useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { CheckCircleOutline } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import { IBAN } from 'ibankit'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useUserBankAccount } from 'services/profile/hooks/useUserBankAccount'
import useUsersProfile from 'services/profile/hooks/useUsersProfile'
import theme from 'styles/theme'

import { azureButtonSx, profileFieldSx } from './userForm.styles'

interface UserDetailsBankAccountProps {
  onBack: () => void
}

interface BankAccountFormValues {
  hasForeignBankAccount: boolean
  bankAccountIban: string
  bankAccountSwift: string
  bankName: string
  bankAccountData: string
}

const UserDetailsBankAccount: FC<UserDetailsBankAccountProps> = ({ onBack }) => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { userBankAccount, isLoading, usersBankAccountUpdate } = useUserBankAccount()
  const { data: userProfile } = useUsersProfile()
  const [initialValues, setInitialValues] = useState<BankAccountFormValues | null>(null)
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'

  const validationSchema = Yup.object().shape({
    hasForeignBankAccount: Yup.boolean(),
    bankAccountIban: Yup.string().when('hasForeignBankAccount', {
      is: false,
      then: (schema) =>
        schema
          .required(
            formatMessage({
              id: 'userDetailsBankAccount.ibanRequired',
              defaultMessage: 'Numer IBAN jest wymagany'
            })
          )
          .test(
            'is-valid-iban',
            formatMessage({
              id: 'validation.iban.invalid',
              defaultMessage: 'Nieprawidłowy numer IBAN'
            }),
            function (value) {
              if (!value) return true
              const cleanValue = value.replace(/\s/g, '').toUpperCase()
              try {
                return IBAN.isValid(cleanValue)
              } catch {
                return false
              }
            }
          )
    }),
    bankAccountSwift: Yup.string().when('hasForeignBankAccount', {
      is: true,
      then: (schema) =>
        schema.required(
          formatMessage({
            id: 'userDetailsBankAccount.swiftRequired',
            defaultMessage: 'Kod SWIFT jest wymagany'
          })
        )
    }),
    bankName: Yup.string().when('hasForeignBankAccount', {
      is: true,
      then: (schema) =>
        schema.required(
          formatMessage({
            id: 'userDetailsBankAccount.bankNameRequired',
            defaultMessage: 'Nazwa banku jest wymagana'
          })
        )
    }),
    bankAccountData: Yup.string().when('hasForeignBankAccount', {
      is: true,
      then: (schema) =>
        schema.required(
          formatMessage({
            id: 'userDetailsBankAccount.bankAccountDataRequired',
            defaultMessage: 'Numer konta jest wymagany'
          })
        )
    })
  })

  const formik = useFormik<BankAccountFormValues>({
    initialValues: {
      hasForeignBankAccount: userBankAccount?.hasForeignBankAccount || false,
      bankAccountIban: userBankAccount?.bankAccountIban || '',
      bankAccountSwift: userBankAccount?.bankAccountSwift || '',
      bankName: userBankAccount?.bankName || '',
      bankAccountData: userBankAccount?.bankAccountData || ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await usersBankAccountUpdate.mutateAsync({
        hasForeignBankAccount: values.hasForeignBankAccount,
        bankAccountIban: values.hasForeignBankAccount ? null : values.bankAccountIban || null,
        bankAccountSwift: values.hasForeignBankAccount ? values.bankAccountSwift || null : null,
        bankName: values.hasForeignBankAccount ? values.bankName || null : null,
        bankAccountData: values.hasForeignBankAccount ? values.bankAccountData || null : null
      })
    }
  })

  useEffect(() => {
    if (userBankAccount && !initialValues) {
      const values = {
        hasForeignBankAccount: userBankAccount.hasForeignBankAccount || false,
        bankAccountIban: userBankAccount.bankAccountIban || '',
        bankAccountSwift: userBankAccount.bankAccountSwift || '',
        bankName: userBankAccount.bankName || '',
        bankAccountData: userBankAccount.bankAccountData || ''
      }
      setInitialValues(values)
      formik.setValues(values)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBankAccount, initialValues])

  const { values, handleSubmit, handleChange, touched, errors, setFieldValue, isSubmitting } = formik

  const isFormModified = useMemo(() => {
    if (!initialValues) return false
    return (
      values.hasForeignBankAccount !== initialValues.hasForeignBankAccount ||
      values.bankAccountIban !== initialValues.bankAccountIban ||
      values.bankAccountSwift !== initialValues.bankAccountSwift ||
      values.bankName !== initialValues.bankName ||
      values.bankAccountData !== initialValues.bankAccountData
    )
  }, [values, initialValues])

  const isDataFilled = useMemo(() => {
    if (values.hasForeignBankAccount) {
      return !!(values.bankAccountSwift && values.bankName && values.bankAccountData)
    }
    return !!values.bankAccountIban
  }, [values])

  const confirmedAt = userProfile?.bankAccountConfirmedAt
    ? dayjs(userProfile.bankAccountConfirmedAt).format('DD.MM.YYYY')
    : null

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='240px'>
        <CircularProgress />
      </Box>
    )
  }

  const content = (
    <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: '560px' }}>
      <FormControlLabel
        control={
          <Switch
            checked={values.hasForeignBankAccount}
            onChange={(e) => setFieldValue('hasForeignBankAccount', e.target.checked)}
            name='hasForeignBankAccount'
            sx={{
              width: '48px',
              height: '26px',
              padding: 0,
              mr: '14px',
              '& .MuiSwitch-switchBase': {
                padding: '2px',
                '&.Mui-checked': {
                  transform: 'translateX(22px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: theme.r4r.navy,
                    opacity: 1
                  }
                }
              },
              '& .MuiSwitch-thumb': {
                width: '22px',
                height: '22px',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.2)'
              },
              '& .MuiSwitch-track': {
                borderRadius: '13px',
                backgroundColor: theme.r4r.greyLight,
                opacity: 1
              }
            }}
          />
        }
        label={formatMessage({
          id: 'userDetailsBankAccount.foreignAccount',
          defaultMessage: 'Posiadam konto w banku zagranicznym'
        })}
        sx={{
          ml: 0,
          mb: '24px',
          alignItems: 'center',
          '& .MuiFormControlLabel-label': {
            fontSize: '14px',
            color: theme.r4r.ink
          }
        }}
      />

      {!!confirmedAt && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: theme.r4r.green,
            px: '20px',
            pt: '18px',
            pb: '14px',
            mb: '24px'
          }}>
          <CheckCircleOutline sx={{ fontSize: '18px', color: '#fff' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
            {formatMessage({
              id: 'userDetailsBankAccount.confirmed',
              defaultMessage: 'Dane bankowe zostały potwierdzone'
            })}
            : {confirmedAt}
          </Typography>
        </Box>
      )}

      {!values.hasForeignBankAccount ? (
        <TextField
          fullWidth
          name='bankAccountIban'
          value={values.bankAccountIban}
          onChange={handleChange}
          error={touched.bankAccountIban && Boolean(errors.bankAccountIban)}
          helperText={touched.bankAccountIban && errors.bankAccountIban}
          label={formatMessage({
            id: 'userDetailsBankAccount.iban',
            defaultMessage: 'Numer IBAN'
          })}
          placeholder='PL00 0000 0000 0000 0000 0000 0000'
          InputLabelProps={{ shrink: true }}
          sx={{
            ...profileFieldSx(false, labelBg),
            '& .MuiOutlinedInput-input': {
              ...profileFieldSx(false, labelBg)['& .MuiOutlinedInput-input'],
              letterSpacing: '0.3px'
            }
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <TextField
            fullWidth
            name='bankName'
            value={values.bankName}
            onChange={handleChange}
            error={touched.bankName && Boolean(errors.bankName)}
            helperText={touched.bankName && errors.bankName}
            label={formatMessage({
              id: 'userDetailsBankAccount.bankName',
              defaultMessage: 'Nazwa banku'
            })}
            InputLabelProps={{ shrink: true }}
            sx={profileFieldSx(false, labelBg)}
          />
          <TextField
            fullWidth
            name='bankAccountSwift'
            value={values.bankAccountSwift}
            onChange={handleChange}
            error={touched.bankAccountSwift && Boolean(errors.bankAccountSwift)}
            helperText={touched.bankAccountSwift && errors.bankAccountSwift}
            label={formatMessage({
              id: 'userDetailsBankAccount.swift',
              defaultMessage: 'Kod SWIFT'
            })}
            InputLabelProps={{ shrink: true }}
            sx={profileFieldSx(false, labelBg)}
          />
          <TextField
            fullWidth
            name='bankAccountData'
            value={values.bankAccountData}
            onChange={handleChange}
            error={touched.bankAccountData && Boolean(errors.bankAccountData)}
            helperText={touched.bankAccountData && errors.bankAccountData}
            label={formatMessage({
              id: 'userDetailsBankAccount.accountNumber',
              defaultMessage: 'Numer konta'
            })}
            InputLabelProps={{ shrink: true }}
            sx={profileFieldSx(false, labelBg)}
          />
        </Box>
      )}

      <LoadingButton
        fullWidth
        type='submit'
        loading={isSubmitting}
        disabled={isSubmitting}
        sx={{ ...azureButtonSx, mt: '24px' }}>
        {formatMessage({
          id:
            isDataFilled && !isFormModified
              ? 'userDetailsBankAccount.confirm'
              : 'userDetailsBankAccount.submit',
          defaultMessage:
            isDataFilled && !isFormModified ? 'Potwierdzam dane' : 'Zawnioskuj o zmianę danych'
        })}
      </LoadingButton>
    </Box>
  )

  if (isDesktop) {
    return <FormikProvider value={formik}>{content}</FormikProvider>
  }

  return (
    <FormikProvider value={formik}>
      <BackNavigationHeader
        label={formatMessage({
          id: 'userDetailsBankAccount.label',
          defaultMessage: 'Dane bankowe'
        })}
        onBack={onBack}
      />
      <BackNavigationContent>{content}</BackNavigationContent>
    </FormikProvider>
  )
}

export default UserDetailsBankAccount
